import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useOutletContext } from 'react-router-dom'
import type { SettingsOutletContext } from './Setting.page'
import SettingsSkeleton, {
  type SettingsSkeletonVariant,
} from './SettingsSkeleton.page'
import { waitAtLeast } from '@utils/waitAtLeast.util'

const SETTINGS_SKELETON_DURATION_MS = 500

const SETTINGS_TABLE_SKELETON_IDS = new Set([
  'practice-room-operatories',
  'staff-list',
  'staff-work-sched',
  'staff-permissions',
  'clinical-procedure',
  'clinical-dicom-capture',
  'accounting-discounts',
  'accounting-membership',
  'accounting-adjustments',
  'accounting-carrier',
  'accounting-fee-schedule',
  'comm-phone-management',
  'comm-call-flow',
  'comm-call-block',
  'comm-devices',
  'comm-call-record',
  'comm-fax',
  'system-cloud-storage',
])

type SettingsSubpageLoadContextValue = {
  setSourceLoading: (sourceId: string, loading: boolean | null) => void
}

const SettingsSubpageLoadContext =
  createContext<SettingsSubpageLoadContextValue | null>(null)

export function useSettingsSubpageLoading(loading: boolean): void {
  const ctx = useContext(SettingsSubpageLoadContext)
  const sourceId = useId()
  const setSourceLoading = ctx?.setSourceLoading

  useEffect(() => {
    if (!setSourceLoading) return
    setSourceLoading(sourceId, loading)
  }, [setSourceLoading, sourceId, loading])

  useEffect(() => {
    if (!setSourceLoading) return
    return (): void => {
      setSourceLoading(sourceId, null)
    }
  }, [setSourceLoading, sourceId])
}

type SettingsSubpageGateProps = {
  pageKey: string
  children: React.ReactNode
}

export function getSettingsSkeletonVariant(
  pageKey: string,
): SettingsSkeletonVariant {
  if (pageKey === 'accounting-integrations') return 'cards'
  if (pageKey === 'clinical-bundled-procedures') return 'procedure-cards'
  if (pageKey === 'reporting-dashboard') return 'tiles'
  if (pageKey === 'reporting-goals') return 'goals'
  return SETTINGS_TABLE_SKELETON_IDS.has(pageKey) ? 'table' : 'form'
}

function SettingsSubpageGate({
  pageKey,
  children,
}: SettingsSubpageGateProps): React.ReactElement {
  const { isPageReady } = useOutletContext<SettingsOutletContext>()
  const [isSubpageSkeleton, setIsSubpageSkeleton] = useState(true)
  const [skeletonKey, setSkeletonKey] = useState(0)
  const [hasLoadSources, setHasLoadSources] = useState(false)
  const [childLoading, setChildLoading] = useState(false)
  const sourcesRef = useRef<Map<string, boolean>>(new Map())
  const skeletonStartedAtRef = useRef(Date.now())

  const setSourceLoading = useCallback(
    (sourceId: string, loading: boolean | null) => {
      if (loading === null) {
        sourcesRef.current.delete(sourceId)
      } else {
        sourcesRef.current.set(sourceId, loading)
      }
      const hasSources = sourcesRef.current.size > 0
      let anyLoading = false
      sourcesRef.current.forEach((isLoading) => {
        if (isLoading) anyLoading = true
      })
      setHasLoadSources(hasSources)
      setChildLoading(anyLoading)
    },
    [],
  )

  const loadContextValue = useMemo(
    () => ({ setSourceLoading }),
    [setSourceLoading],
  )

  useEffect(() => {
    if (!isPageReady) return
    skeletonStartedAtRef.current = Date.now()
    setIsSubpageSkeleton(true)
    setSkeletonKey((key) => key + 1)
  }, [pageKey, isPageReady])

  useEffect(() => {
    if (!isPageReady || !isSubpageSkeleton) return
    let anyLoading = false
    sourcesRef.current.forEach((isLoading) => {
      if (isLoading) anyLoading = true
    })
    if (sourcesRef.current.size > 0 && anyLoading) return
    let cancelled = false
    void waitAtLeast(
      skeletonStartedAtRef.current,
      SETTINGS_SKELETON_DURATION_MS,
    ).then(() => {
      if (cancelled) return
      setIsSubpageSkeleton(false)
    })
    return () => {
      cancelled = true
    }
  }, [
    isPageReady,
    isSubpageSkeleton,
    skeletonKey,
    hasLoadSources,
    childLoading,
  ])

  const showSkeleton = !isPageReady || isSubpageSkeleton

  return (
    <SettingsSubpageLoadContext.Provider value={loadContextValue}>
      <div className="settings-subpage-gate">
        {showSkeleton ? (
          <SettingsSkeleton
            variant={getSettingsSkeletonVariant(pageKey)}
            pageKey={pageKey}
          />
        ) : null}
        <div
          className={`settings-subpage-gate__page${
            showSkeleton ? ' is-pending' : ''
          }`}
          aria-hidden={showSkeleton}
        >
          {children}
        </div>
      </div>
    </SettingsSubpageLoadContext.Provider>
  )
}

export default SettingsSubpageGate
